<template>
  <div class="pdf-wrapper">
    <div v-if="!hideToolbar && pdfError == null" class="pdf-toolbar">
      <LISAButton
        icon="fas fa-minus"
        text
        size="small"
        :disabled="pdfLoading"
        @click.stop="zoomOut"
      />
      <span class="pdf-zoom-label text-primary">{{ Math.round(scale * 100) }}%</span>
      <LISAButton
        text
        icon="fas fa-plus"
        size="small"
        :disabled="pdfLoading"
        @click.stop="zoomIn"
      />

      <LISAButton text size="small" :disabled="pdfLoading" @click.stop="fitWidth">
        {{ t("modules.filemanagement.components.FMFileViewer.FMFileViewerPDF.fit") }}
      </LISAButton>
      <LISAButton text size="small" :disabled="pdfLoading" @click.stop="scale = 1">
        100%
      </LISAButton>

      <div class="pdf-sep"></div>

      <div class="flex-1"></div>

      <span class="text-primary">
        {{ pageCount }}
        {{ t("modules.filemanagement.components.FMFileViewer.FMFileViewerPDF.pages", pageCount) }}
      </span>
    </div>

    <div ref="scrollEl" class="pdf-scroll" :class="{ 'overflow-hidden': overflowHidden }">
      <FMFileViewerError v-if="pdfError != null" />

      <div
        v-for="pageNo in pageCount"
        :key="pageNo"
        :ref="(el) => setPageRef(el, pageNo - 1)"
        class="pdf-page"
      >
        <FMFileViewerPDFPageCanvas
          :pdf="pdfDoc"
          :page-number="pageNo"
          :scale="scale"
          fit-mode="contain"
          :fit-width="fitSize.width"
          :fit-height="fitSize.height"
          :big-shadow="bigShadow"
          :active="isPageActive(pageNo)"
          :keep-rendered="true"
          :recently-changed="recentlyChanged"
          @rendered="onPageRendered"
        />
      </div>

      <div v-if="pdfLoading" class="pdf-loading">
        <ProgressSpinner />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import ProgressSpinner from "primevue/progressspinner";
import { computed, onBeforeUnmount, onMounted, ref, watch, type PropType } from "vue";
import LISAButton from "@/components/shared/primevue/LISAButton.vue";
import { usePdfDocument } from "./usePdfDocument";
import FMFileViewerError from "../FMFileViewerError.vue";
import FMFileViewerPDFPageCanvas from "./FMFileViewerPDFPageCanvas.vue";

type FMFileMeta = {
  pages: number;
  customPageOrder: number[];
  transformations: {
    type: "rotate";
    amount: number;
  }[];
};

const props = defineProps({
  fileUUID: {
    type: String,
    required: true,
  },
  meta: {
    type: Object as PropType<FMFileMeta>,
    required: false,
    default: undefined,
  },
  hideToolbar: {
    type: Boolean,
    required: false,
    default: false,
  },
  overflowHidden: {
    type: Boolean,
    required: false,
    default: false,
  },
  recentlyChanged: {
    type: Boolean,
    required: false,
    default: false,
  },
  bigShadow: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const scale = ref(1);
const scrollEl = ref<HTMLDivElement | null>(null);
const pageRefs = ref<(HTMLDivElement | null)[]>([]);
const fitSize = ref({ width: 1, height: 1 });

const currentPage = ref(1);
const pageInput = ref("1");

const renderedPageInfo = ref<Record<number, { width: number; height: number; scale: number }>>({});

const pageRenderBuffer = 2;

const { pdfLoading, pdfError, pageCount, pdfDoc } = usePdfDocument(computed(() => props.fileUUID));

function setPageRef(el: any, index: number) {
  pageRefs.value[index] = el as HTMLDivElement | null;
}

function isPageActive(page: number) {
  return Math.abs(page - currentPage.value) <= pageRenderBuffer;
}

function updateCurrentPageFromScroll() {
  const container = scrollEl.value;
  if (!container) {
    return;
  }

  const scrollTop = container.scrollTop;
  let bestPage = 1;
  let bestDist = Number.POSITIVE_INFINITY;

  for (let i = 0; i < pageRefs.value.length; i++) {
    const pageEl = pageRefs.value[i];
    if (!pageEl) {
      continue;
    }

    const dist = Math.abs(pageEl.offsetTop - scrollTop);
    if (dist < bestDist) {
      bestDist = dist;
      bestPage = i + 1;
    }
  }

  currentPage.value = bestPage;
  pageInput.value = String(bestPage);
}

let scrollRaf = 0;
let resizeObserver: ResizeObserver | null = null;

const onScroll = () => {
  cancelAnimationFrame(scrollRaf);
  scrollRaf = requestAnimationFrame(updateCurrentPageFromScroll);
};

function zoomIn() {
  scale.value = Math.min(3, +(scale.value + 0.25).toFixed(2));
}

function zoomOut() {
  scale.value = Math.max(0.5, +(scale.value - 0.25).toFixed(2));
}

function fitWidth() {
  const firstRendered = renderedPageInfo.value[1];

  if (!firstRendered?.width) {
    return;
  }

  const factor = fitSize.value.width / firstRendered.width;

  scale.value = Math.min(3, Math.max(0.5, +(scale.value * factor).toFixed(2)));
}

function updateFitSize() {
  const container = scrollEl.value;

  if (!container) {
    return;
  }

  fitSize.value = {
    width: Math.max(1, container.clientWidth - 24),
    height: Math.max(1, container.clientHeight - 64),
  };
}

function onPageRendered(payload: {
  pageNumber: number;
  width: number;
  height: number;
  scale: number;
}) {
  renderedPageInfo.value[payload.pageNumber] = {
    width: payload.width,
    height: payload.height,
    scale: payload.scale,
  };
}

watch(
  () => [props.fileUUID, pageCount.value] as const,
  () => {
    pageRefs.value = [];
    renderedPageInfo.value = {};
    currentPage.value = 1;
    pageInput.value = "1";
  },
);

onMounted(() => {
  scrollEl.value?.addEventListener("scroll", onScroll, { passive: true });
  updateFitSize();

  if (typeof ResizeObserver !== "undefined" && scrollEl.value) {
    resizeObserver = new ResizeObserver(updateFitSize);
    resizeObserver.observe(scrollEl.value);
  }
});

onBeforeUnmount(() => {
  cancelAnimationFrame(scrollRaf);
  scrollEl.value?.removeEventListener("scroll", onScroll);
  resizeObserver?.disconnect();
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.pdf-toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--surface-card, #fff);
  border-bottom: 1px solid var(--surface-border, #ddd);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  font-size: 0.9em;
  min-height: 0;
}

.pdf-pagejump {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 0;
}

.pdf-zoom-label {
  min-width: 70px;
  text-align: center;
}

.pdf-muted {
  opacity: 0.7;
}

.pdf-sep {
  width: 1px;
  height: 20px;
  background: var(--surface-border, #ddd);
  margin: 0 4px;
}

.pdf-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
}

.pdf-scroll {
  flex: 1;
  padding: 8px 0;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
}

.pdf-page {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 0;
  min-height: 100%;
  box-sizing: border-box;
}

.pdf-page :deep(.pdf-page-canvas) {
  flex: 1;
  width: 100%;
  min-height: 100%;
}

.pdf-loading {
  display: flex;
  justify-content: center;
  padding: 16px;
}
</style>
